import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

const CATEGORIES = ["SQL", "Python", "Excel", "Power BI", "Machine Learning", "Geral"];
const DIFFICULTIES = ["Iniciante", "Intermediário", "Avançado"];

interface Dataset {
  id: string;
  title: string;
  description: string;
  external_url: string;
  category: string;
  difficulty: string;
  source: string | null;
  is_active: boolean;
}

interface AddDatasetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  editingDataset?: Dataset | null;
}

export function AddDatasetDialog({ open, onOpenChange, onSuccess, editingDataset }: AddDatasetDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [category, setCategory] = useState("Geral");
  const [difficulty, setDifficulty] = useState("Iniciante");
  const [source, setSource] = useState("");

  useEffect(() => {
    if (editingDataset) {
      setTitle(editingDataset.title);
      setDescription(editingDataset.description);
      setExternalUrl(editingDataset.external_url);
      setCategory(editingDataset.category);
      setDifficulty(editingDataset.difficulty);
      setSource(editingDataset.source || "");
    } else {
      setTitle("");
      setDescription("");
      setExternalUrl("");
      setCategory("Geral");
      setDifficulty("Iniciante");
      setSource("");
    }
  }, [editingDataset, open]);

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim() || !externalUrl.trim()) {
      toast({ title: "Preencha os campos obrigatórios", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        external_url: externalUrl.trim(),
        category,
        difficulty,
        source: source.trim() || null,
        updated_at: new Date().toISOString(),
      };

      if (editingDataset) {
        const { error } = await supabase
          .from("community_datasets" as any)
          .update(payload as any)
          .eq("id", editingDataset.id);
        if (error) throw error;
        toast({ title: "Dataset atualizado com sucesso!" });
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        const { error } = await supabase
          .from("community_datasets" as any)
          .insert({ ...payload, created_by: user?.id } as any);
        if (error) throw error;
        toast({ title: "Dataset adicionado com sucesso!" });
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Erro ao salvar dataset:", error);
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editingDataset ? "Editar Dataset" : "Adicionar Dataset"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Título *</Label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Vendas de E-commerce Brasil" />
          </div>
          <div>
            <Label htmlFor="description">Descrição *</Label>
            <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descreva o dataset e sugestões de uso..." rows={3} />
          </div>
          <div>
            <Label htmlFor="url">URL externa *</Label>
            <Input id="url" value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} placeholder="https://kaggle.com/..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Categoria</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dificuldade</Label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DIFFICULTIES.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="source">Fonte (opcional)</Label>
            <Input id="source" value={source} onChange={(e) => setSource(e.target.value)} placeholder="Ex: Kaggle, dados.gov.br, GitHub" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Salvando..." : editingDataset ? "Salvar" : "Adicionar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
